// src/helpers/native-biometric.ts
import { isNative } from '../platform';
import { NativeBiometric as _NativeBiometric } from '@capgo/capacitor-native-biometric';

// Capacitor's registerPlugin() Proxy has a default `get` handler that returns
// a callable for ANY property — including `then`. This makes the proxy look
// like a thenable to `await` / `Promise.resolve()`, which triggers
// "NativeBiometric.then() is not implemented on android". We wrap it to
// neutralize `.then` so the proxy is never accidentally treated as a Promise.
const NativeBiometric = new Proxy(_NativeBiometric, {
  get(target, prop, receiver) {
    if (prop === 'then') return undefined;
    return Reflect.get(target, prop, receiver);
  },
});

const SERVER = 'com.mailander.app';

/**
 * Maps the numeric BiometryType from @capgo/capacitor-native-biometric to a
 * human-readable category used by BiometricsStorage.
 *
 * Plugin BiometryType enum values:
 *   1 = TOUCH_ID (iOS)
 *   2 = FACE_ID (iOS)
 *   3 = FINGERPRINT (Android)
 *   4 = FACE_AUTHENTICATION (Android)
 *   5 = IRIS_AUTHENTICATION (Android)
 *   6 = MULTIPLE (Android — multiple biometrics enrolled)
 */
export function mapBiometryType(biometryType: number | undefined | null): 'face' | 'fingerprint' | 'unknown' {
  switch (biometryType) {
    case 1: // TOUCH_ID
    case 3: // FINGERPRINT
      return 'fingerprint';
    case 2: // FACE_ID
    case 4: // FACE_AUTHENTICATION
      return 'face';
    case 5: // IRIS_AUTHENTICATION
      return 'fingerprint'; // closest category
    case 6: // MULTIPLE — device has more than one biometric; fingerprint is most common
      return 'fingerprint';
    default:
      return 'unknown';
  }
}

export type NativeBiometricAvailability = {
  available: boolean;
  biometryType: 'face' | 'fingerprint' | 'unknown';
  rawBiometryType: number | null;
};

export async function isNativeBiometricAvailable(): Promise<NativeBiometricAvailability> {
  if (!isNative()) return { available: false, biometryType: 'unknown', rawBiometryType: null };
  try {
    const result = await NativeBiometric.isAvailable({ useFallback: true });
    console.log('[NativeBiometric] isAvailable result:', JSON.stringify(result));
    const raw = (result as any).biometryType ?? null;
    return {
      available: result.isAvailable,
      biometryType: mapBiometryType(raw),
      rawBiometryType: raw,
    };
  } catch (err) {
    console.warn('[NativeBiometric] isAvailable error:', err);
    return { available: false, biometryType: 'unknown', rawBiometryType: null };
  }
}

/**
 * Show native fingerprint/face prompt.
 * Resolves on success, throws on cancel/failure.
 */
export async function promptNativeBiometric(reason: string): Promise<void> {
  console.log('[NativeBiometric] verifyIdentity starting…');
  const verify = NativeBiometric.verifyIdentity({
    reason,
    title: 'Mailander',
    subtitle: 'Biometric Authentication',
    description: reason,
    maxAttempts: 3,
    useFallback: true,
  });
  const timeout = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error('Biometric prompt timed out')), 30_000),
  );
  await Promise.race([verify, timeout]);
  console.log('[NativeBiometric] verifyIdentity succeeded');
}

/**
 * Store user credentials in Android Keystore / iOS Keychain.
 * Called after successful login or registration.
 */
export async function storeNativeCredentials(userId: string, refreshToken: string): Promise<void> {
  await NativeBiometric.setCredentials({
    username: userId,
    password: refreshToken,
    server: SERVER,
  });
}

/**
 * Retrieve stored credentials after biometric verification.
 */
export async function getNativeCredentials(): Promise<{ userId: string; refreshToken: string } | null> {
  try {
    const creds = await NativeBiometric.getCredentials({ server: SERVER });
    if (!creds.username || !creds.password) return null;
    return { userId: creds.username, refreshToken: creds.password };
  } catch {
    return null;
  }
}

/**
 * Clear stored credentials (logout, disable biometrics).
 */
export async function clearNativeCredentials(): Promise<void> {
  try {
    await NativeBiometric.deleteCredentials({ server: SERVER });
  } catch {
    // Ignore — credentials may not exist
  }
}
